/**
 * src/gm/kernel/state-machine.ts — 五态断言与查询（继承旧 game-master.ts 断言层）。
 *
 * 状态机只前进不后退；放弃 = 物理删除（无放弃态）。非法迁移直接抛 GmError——
 * 内核 drain 会把执行期的 BAD_STATUS 翻译为 deferred（设计稿 §4.2 推迟语义）。
 */

import { GmError } from '../platform/errors'
import { ROLE_LABEL } from '../platform/model-map'
import type {
  Decision,
  Employee,
  EmployeeRole,
  Project,
  SaveBundle,
} from '../platform/save-types'

export function assertStatus(project: Project, expected: Project['status'], action: string): void {
  if (project.status !== expected) {
    throw new GmError(
      'BAD_STATUS',
      `现在不是「${action}」的时机（项目当前阶段不允许这一步）。刷新一下看看项目面板？`,
      { expected, actual: project.status },
    )
  }
}

export function findProject(bundle: SaveBundle, projectId: string): Project {
  const p = bundle.projects.projects.find((x) => x.id === projectId)
  if (!p) throw new GmError('PROJECT_NOT_FOUND', '这盘卡带的档案不见了——刷新页面看看，或者它已经被结算/放弃了。')
  return p
}

export function findTeamEmployee(bundle: SaveBundle, project: Project, role: EmployeeRole): Employee {
  const emp = bundle.employees.employees.find((e) => e.id === project.team[role])
  if (!emp) {
    const code = role === 'designer' ? 'MISSING_DESIGNER' : 'MISSING_TEAM'
    throw new GmError(code, `${ROLE_LABEL[role]}岗位档案缺失，开不了工——去「人事部」看看人在不在。`)
  }
  return emp
}

export function resolvedDecisionsOf(bundle: SaveBundle, project: Project): Decision[] {
  return project.decision_ids
    .map((id) => bundle.decisions.decisions.find((d) => d.id === id))
    .filter((d): d is Decision => !!d && d.status === 'resolved')
}

export function allDecisionsResolved(bundle: SaveBundle, project: Project): boolean {
  return resolvedDecisionsOf(bundle, project).length === project.decision_ids.length
}
